import {
  actionNames,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";
import { useRouter } from "next/router";
import { useEffect } from "react";

import Article from "@/types/models/Article.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import {
  ILegacyStudentReadingLevel,
  IRecommendedReadingLevel,
} from "@/types/models/ReadingLevel.interface";
import { User } from "@/types/models/User.interface";
import getUserProperties from "@/users/getUserProperties";
import getCurrentLevel from "@/utils/getCurrentLevel/getCurrentLevel";
import sentenceCase from "@/utils/sentenceCase/index";
import { ContentType } from "@/view/constants/contentTypes";
import { TCurrentLevel } from "@/view/utils/useUpdateLevel";

export interface IContent {
  data: Article;
  id: string;
  content_type: ContentType;
}

const useSetInitialLevel = (
  {
    content,
    publishedUserAssignments = [] as Assignment[],
    setCurrentLevel,
    user,
    studentReadingLevel,
  }: {
    content: IContent;
    publishedUserAssignments: Assignment[];
    setCurrentLevel: (level: TCurrentLevel) => void;
    user: User;
    studentReadingLevel: ILegacyStudentReadingLevel | IRecommendedReadingLevel;
  },
  dependencies = [],
) => {
  const { query } = useRouter();
  const { contentId = "", levelId = "" } = query;
  const levelIdParam = Array.isArray(levelId) ? levelId[0] : levelId;

  const contentIdString =
    (Array.isArray(contentId) ? contentId[0] : contentId) || content.id;

  const { trackAction, getEventName } = useTracker();
  const { isStudent } = getUserProperties(user);

  const levels = content?.data.article_levels || [];

  useEffect(() => {
    if (!levels.length || (isStudent && !studentReadingLevel)) return;
    const newCurrentLevel = getCurrentLevel({
      articleLevels: levels,
      contentId: contentIdString,
      levelId:
        levelIdParam ||
        (studentReadingLevel as IRecommendedReadingLevel)
          ?.recommended_article_level_content_id ||
        "",
      matchedAssignment: publishedUserAssignments?.[0],
      user,
      studentReadingLevel,
    });
    const sentenceCaseContentType = sentenceCase(content?.content_type);
    const levelChangeEventProperties = {
      event_name: getEventName(objectTypes.content, null, actionNames.read),
      event_type: eventTypes.pageView, // Recommended by DAS to send additional page-view event as an exception to the rule
      dimension_properties: {
        object_type: objectTypes.content,
        object_id: content?.id,
        content_id: levelIdParam || "",
        content_type: sentenceCaseContentType,
        description: `${sentenceCaseContentType} loaded in view page.`,
      },
    };

    setCurrentLevel(newCurrentLevel);
    trackAction(levelChangeEventProperties);
  }, [
    levels,
    JSON.stringify(publishedUserAssignments),
    studentReadingLevel,
    ...dependencies,
  ]);
};

export default useSetInitialLevel;
